import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Code2, Sparkles, Loader2, Eye, Trash2 } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import ProblemEditor from "./ProblemEditor";
import ProblemPreview from "./ProblemPreview";

export const CODING_LANGS = [
  { key: "python", label: "Python" },
  { key: "javascript", label: "JavaScript" },
  { key: "cpp", label: "C++" },
  { key: "c", label: "C" },
  { key: "bash", label: "Bash" },
];
const DIFFS = ["easy", "medium", "hard"];
const TIME_LIMITS = [15, 30, 45, 60, 90, 120];
const FIELD = "w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none";
const LABEL = "text-xs text-slate-400 uppercase tracking-wider";

export const codingDefaults = (job = {}) => ({
  coding_topics: job.coding_topics || "",
  coding_difficulty: job.coding_difficulty || "medium",
  coding_languages: job.coding_languages?.length ? job.coding_languages : ["python", "javascript"],
  coding_problems: job.coding_problems || [],
  coding_time_limit_min: job.coding_time_limit_min || 45,
});

export const codingPayload = (v) => ({
  coding_topics: v.coding_topics.trim(),
  coding_difficulty: v.coding_difficulty,
  coding_languages: v.coding_languages,
  coding_problems: v.coding_problems,
  coding_time_limit_min: parseInt(v.coding_time_limit_min || 45),
});

const Pill = ({ on, onClick, children, testid }) => (
  <button type="button" data-testid={testid} aria-pressed={on} onClick={onClick}
    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors capitalize ${on ? "bg-indigo-600 border-indigo-600 text-white" : "border-[color:var(--bd-sub)] text-slate-300 hover:border-indigo-400"}`}>
    {children}
  </button>
);

export default function CodingRoundSection({ value, onChange, ctx, prefix = "job" }) {
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(0);
  const [preview, setPreview] = useState(false);
  const probs = value.coding_problems;
  const set = (patch) => onChange({ ...value, ...patch });

  useEffect(() => {
    if (!value.coding_topics && ctx.must_haves) set({ coding_topics: ctx.must_haves });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLang = (k) => {
    const on = value.coding_languages.includes(k);
    if (on && value.coding_languages.length === 1) return toast.error("Keep at least one language");
    set({ coding_languages: on ? value.coding_languages.filter(l => l !== k) : [...value.coding_languages, k] });
  };

  const generate = async () => {
    if (!value.coding_topics.trim() && !ctx.description.trim()) return toast.error("Add topics to cover first");
    if (probs.length && !window.confirm("Replace the current problems with 3 new AI-generated ones?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/jobs/generate-coding-problems", {
        title: ctx.title, description: ctx.description, topics: value.coding_topics,
        difficulty: value.coding_difficulty, languages: value.coding_languages, count: 3,
      });
      set({ coding_problems: data.problems }); setTab(0);
      toast.success(`Generated ${data.problems.length} problems — review and edit before saving`);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "AI generation failed"); }
    finally { setBusy(false); }
  };

  const updateProb = (i, p) => set({ coding_problems: probs.map((x, j) => (j === i ? p : x)) });
  const removeProb = (i) => { set({ coding_problems: probs.filter((_, j) => j !== i) }); setTab(0); };
  const cur = probs[Math.min(tab, probs.length - 1)];

  return (
    <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/[0.04] p-4 space-y-4" data-testid={`${prefix}-coding-round`}>
      <div className="flex items-center gap-2">
        <Code2 className="w-4 h-4 text-indigo-400" />
        <div className="text-sm font-semibold">Coding Round</div>
        <div className="text-[11px] text-slate-500 ml-auto">Each candidate gets one problem from the pool at random</div>
      </div>
      <div>
        <label className={LABEL}>Topics to cover</label>
        <input data-testid={`${prefix}-coding-topics`} value={value.coding_topics} onChange={(e) => set({ coding_topics: e.target.value })}
          placeholder="REST APIs, SQL joins, async Python" className={FIELD} />
      </div>
      <div className="grid sm:grid-cols-[auto_auto_1fr] gap-4">
        <div>
          <div className={LABEL}>Time limit</div>
          <select data-testid={`${prefix}-coding-time-limit`} value={value.coding_time_limit_min} onChange={(e) => set({ coding_time_limit_min: parseInt(e.target.value) })}
            className="tp-select mt-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none">
            {[...new Set([...TIME_LIMITS, value.coding_time_limit_min])].sort((a, b) => a - b).map(m => <option key={m} value={m}>{m} min</option>)}
          </select>
        </div>
        <div>
          <div className={LABEL}>Difficulty</div>
          <div className="flex gap-1.5 mt-1.5">
            {DIFFS.map(d => <Pill key={d} testid={`${prefix}-coding-diff-${d}`} on={value.coding_difficulty === d} onClick={() => set({ coding_difficulty: d })}>{d}</Pill>)}
          </div>
        </div>
        <div>
          <div className={LABEL}>Languages allowed</div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {CODING_LANGS.map(l => <Pill key={l.key} testid={`${prefix}-coding-lang-${l.key}`} on={value.coding_languages.includes(l.key)} onClick={() => toggleLang(l.key)}>{l.label}</Pill>)}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" data-testid={`${prefix}-coding-generate`} onClick={generate} disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {busy ? "Generating… (about 30s)" : probs.length ? "Regenerate 3 problems with AI" : "Generate 3 problems with AI"}
        </button>
        {cur && (
          <button type="button" data-testid={`${prefix}-coding-preview`} onClick={() => setPreview(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
            <Eye className="w-3.5 h-3.5" /> Preview as candidate
          </button>
        )}
      </div>
      {probs.length === 0 ? (
        <div className="text-[11px] text-slate-500" data-testid={`${prefix}-coding-empty`}>
          No problems yet. If you save without generating, AI will create 3 problems automatically from the topics and job description.
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-1 border-b border-[color:var(--bd-sub)] mb-3">
            {probs.map((p, i) => (
              <button type="button" key={p.id || i} data-testid={`${prefix}-coding-tab-${i}`} onClick={() => setTab(i)}
                className={`px-3 py-2 text-xs font-medium -mb-px border-b-2 transition-colors ${i === tab ? "border-indigo-500 text-indigo-400" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
                Problem {i + 1}
              </button>
            ))}
            <button type="button" data-testid={`${prefix}-coding-remove`} onClick={() => removeProb(tab)} title="Remove this problem"
              className="ml-auto p-1.5 rounded text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <ProblemEditor problem={cur} langs={value.coding_languages} onChange={(p) => updateProb(tab, p)} prefix={prefix} />
        </div>
      )}
      {preview && cur && <ProblemPreview problem={cur} langs={value.coding_languages} onClose={() => setPreview(false)} />}
    </div>
  );
}
